// src/pages/ContentGeneration.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Snackbar,
  Alert,
  IconButton,
  Container,
  Paper,
  Grid,
  Divider,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Schedule as ScheduleIcon,
  Tune as TuneIcon,
  Topic as TopicIcon,
  Search as SearchIcon,
  Call as CallIcon,
  AutoAwesome as AutoAwesomeIcon,
  OpenInNew as OpenInNewIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import axios from 'axios';
import { getApiUrl } from '../config';
import { useNotifications } from '../contexts/NotificationContext';
import PageHead from '../components/PageHead';

function ContentGeneration() {
  const [settings, setSettings] = useState({
    frequency: '',
    tone: '',
    topic: '',
    seoFocus: [],
    ctaText: '',
    ctaButtonColor: '',
    ctaTextColor: '',
  });

  const [recentPosts, setRecentPosts] = useState([]);
  const [upcomingPosts, setUpcomingPosts] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editField, setEditField] = useState('');
  const [editValue, setEditValue] = useState('');
  const [selectedFrequencyDays, setSelectedFrequencyDays] = useState(null);
  const [selectedSeoFocusOptions, setSelectedSeoFocusOptions] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationError, setGenerationError] = useState(null);
  const [blogUrl, setBlogUrl] = useState('');
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const [currentTaskId, setCurrentTaskId] = useState(null);
  const { addNotification } = useNotifications();

  const seoFocusOptions = [
    'Broad Keywords', // For general audience and high-traffic terms
    'Long-Tail Keywords', // For more specific, niche-targeted traffic
    'Local SEO', // For local audience and businesses
    'Competitor-Based Keywords', // For ranking in areas competitors are strong
    'High Conversion Intent Keywords', // Keywords targeted at conversion
    'Niche Topics', // For specialized content
  ];

  // Fetch posts and settings data
  useEffect(() => {
    const token = localStorage.getItem('token');
    // Fetch recent posts
    axios
      .get(`${getApiUrl()}/posts/published-posts`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        setRecentPosts(response.data);
      })
      .catch((error) => {
        console.error('Error fetching recent posts', error);
      });
    // Fetch upcoming posts
    axios
      .get(`${getApiUrl()}/posts/scheduled-posts`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        setUpcomingPosts(response.data);
      })
      .catch((error) => {
        console.error('Error fetching upcoming posts', error);
      });

    // Fetch user settings from the server
    axios.get(`${getApiUrl()}/settings/progress`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
    .then((response) => {
      console.log('Raw API response:', response.data);
      
      // Handle SEO focus data
      let seoFocusArray = [];
      if (response.data.seo_focus) {
        console.log('SEO Focus before processing:', response.data.seo_focus);
        if (typeof response.data.seo_focus === 'string') {
          seoFocusArray = response.data.seo_focus.split(',').map(item => item.trim());
        } else if (Array.isArray(response.data.seo_focus)) {
          seoFocusArray = response.data.seo_focus;
        }
        console.log('SEO Focus after processing:', seoFocusArray);
      }

      const updatedSettings = {
        frequency: response.data.frequency || '',
        tone: response.data.writing_tone || '',
        topic: response.data.topic || '',
        seoFocus: seoFocusArray,
        ctaText: response.data.cta_text || '',
        ctaButtonColor: response.data.cta_button_color || '#1976d2',
        ctaTextColor: response.data.cta_text_color || '#ffffff',
      };
      
      console.log('Final settings being set:', updatedSettings);
      setSettings(updatedSettings);
    })
    .catch((error) => {
      console.error('Error fetching user settings:', error);
    });

    // Fetch blog URL
    axios.get(`${getApiUrl()}/settings/blog-url`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    })
    .then((response) => {
      console.log('Blog URL response:', response.data);
      setBlogUrl(response.data.url);
    })
    .catch((error) => {
      console.error('Error fetching blog URL', error);
    });

    // Cleanup function
    return () => {
      if (currentTaskId) {
        setIsGenerating(false);
        setCurrentTaskId(null);
      }
    };
  }, []);

  const handleEditSetting = (field) => {
    setEditField(field);
    setIsEditing(true);

    if (field === 'frequency') {
      const days = [
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
        'Sunday',
      ];
      let initialSelectedDays = {};

      if (settings.frequency === 'all') {
        initialSelectedDays = {
          Daily: true,
          Monday: true,
          Tuesday: true,
          Wednesday: true,
          Thursday: true,
          Friday: true,
          Saturday: true,
          Sunday: true,
        };
      } else {
        const selectedDaysArray = settings.frequency
          ? settings.frequency.split(', ')
          : [];
        initialSelectedDays = { Daily: false };
        days.forEach((day) => {
          initialSelectedDays[day] = selectedDaysArray.includes(day);
        });
      }
      setSelectedFrequencyDays(initialSelectedDays);
    } else if (field === 'seoFocus') {
      let initialSelectedOptions = {};
      seoFocusOptions.forEach((option) => {
        initialSelectedOptions[option] = settings.seoFocus.includes(option);
      });
      setSelectedSeoFocusOptions(initialSelectedOptions);
    } else if (field === 'cta') {
      setEditValue({
        ctaText: settings.ctaText || '',
        ctaButtonColor: settings.ctaButtonColor || '#1976d2',
        ctaTextColor: settings.ctaTextColor || '#ffffff',
      });
    } else {
      setEditValue(settings[field]);
    }
  };

  const handleSaveSetting = () => {
    if (editField === 'frequency') {
      let updatedFrequency = '';
      if (selectedFrequencyDays['Daily']) {
        updatedFrequency = 'all';
      } else {
        const days = [
          'Monday',
          'Tuesday',
          'Wednesday',
          'Thursday',
          'Friday',
          'Saturday',
          'Sunday',
        ];
        const selectedDaysList = days.filter(
          (day) => selectedFrequencyDays[day]
        );
        updatedFrequency = selectedDaysList.join(', ');
      }
      const updatedSettings = { ...settings, frequency: updatedFrequency };
      setSettings(updatedSettings);

      // Update settings in backend
      axios
        .post(
          `${getApiUrl()}/settings/`,
          {
            posting_frequency: updatedFrequency,
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
          }
        )
        .then((response) => {
          console.log('Settings updated successfully');
        })
        .catch((error) => {
          console.error('Error updating settings', error);
        });
    } else if (editField === 'seoFocus') {
      const updatedSeoFocus = seoFocusOptions.filter(
        (option) => selectedSeoFocusOptions[option]
      );
      const updatedSettings = { ...settings, seoFocus: updatedSeoFocus };
      setSettings(updatedSettings);

      // Update settings in backend
      axios.post(
        `${getApiUrl()}/settings/`,
        {
          seo_focus: updatedSeoFocus.join(','),
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
    } else if (editField === 'topic') {
      const updatedSettings = { ...settings, topic: editValue };
      setSettings(updatedSettings);

      // Update settings in backend
      axios.post(
        `${getApiUrl()}/settings/`,
        {
          topic: editValue,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
    } else if (editField === 'tone') {
      const updatedSettings = { ...settings, tone: editValue };
      setSettings(updatedSettings);

      // Update settings in backend
      axios
        .post(
          `${getApiUrl()}/settings/`,
          {
            writing_tone: editValue,
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
          }
        )
        .then((response) => {
          console.log('Settings updated successfully');
        })
        .catch((error) => {
          console.error('Error updating settings', error);
        });
    } else if (editField === 'cta') {
      const updatedSettings = {
        ...settings,
        ctaText: editValue.ctaText,
        ctaButtonColor: editValue.ctaButtonColor,
        ctaTextColor: editValue.ctaTextColor,
      };
      setSettings(updatedSettings);

      // Update settings in the backend
      axios
        .post(
          `${getApiUrl()}/settings/`,
          {
            cta: {
              text: editValue.ctaText,
              button_color: editValue.ctaButtonColor,
              text_color: editValue.ctaTextColor,
            },
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
          }
        )
        .then((response) => {
          console.log('CTA settings updated successfully');
        })
        .catch((error) => {
          console.error('Error updating CTA settings', error);
        });
    } else {
      const updatedSettings = { ...settings, [editField]: editValue };
      setSettings(updatedSettings);

      // Update settings in the backend
      axios
        .post(
          `${getApiUrl()}/settings/`,
          {
            [editField]: editValue,
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
          }
        )
        .then((response) => {
          console.log('Settings updated successfully');
        })
        .catch((error) => {
          console.error('Error updating settings', error);
        });
    }

    setIsEditing(false);
  };

  const displayFrequency = (frequency) => {
    if (!frequency) {
      return '';
    }
    if (frequency === 'all') {
      return 'Daily';
    }
    return frequency;
  };

  // Add polling function for task status
  const pollTaskStatus = async (taskId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${getApiUrl()}/posts/task-status/${taskId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const { status, post, result } = response.data;

      if (status === 'completed' && post) {
        // Schedule the post for tomorrow
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(9, 0, 0, 0); // Set to 9 AM
        const scheduledDate = tomorrow.toISOString().split('.')[0];

        try {
          // Update the post status to scheduled
          await axios.patch(
            `${getApiUrl()}/posts/${post.id}/update-status`,
            {
              status: 'scheduled',
              scheduled_date: scheduledDate
            },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          // Refresh the posts lists
          await fetchPosts();
          
          addNotification({
            message: 'Blog post generated and scheduled successfully! Click the refresh button to see updates.',
            type: 'success'
          });
          setCurrentTaskId(null);
          setGenerationError(null);
          return true;
        } catch (err) {
          setGenerationError('Failed to schedule the generated post');
          addNotification({
            message: 'Failed to schedule the generated post',
            type: 'error'
          });
          setCurrentTaskId(null);
          return true;
        }
      } else if (status === 'completed' && result && !result.success) {
        setGenerationError(result.error || 'Failed to generate post');
        addNotification({
          message: result.error || 'Failed to generate post',
          type: 'error'
        });
        setCurrentTaskId(null);
        return true;
      } else if (status === 'failed') {
        setGenerationError(result?.error || 'Failed to generate post');
        addNotification({
          message: result?.error || 'Failed to generate post',
          type: 'error'
        });
        setCurrentTaskId(null);
        return true;
      }

      return false;
    } catch (error) {
      console.error('Error polling task status:', error);
      setGenerationError('Error checking task status');
      addNotification({
        message: 'Error checking task status',
        type: 'error'
      });
      setCurrentTaskId(null);
      return true;
    }
  };

  const handleCreatePost = async () => {
    setIsGenerating(true);
    setGenerationError(null);
    
    try {
      const response = await axios.post(
        `${getApiUrl()}/posts/generate`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      if (response.data.data?.task_id) {
        const taskId = response.data.data.task_id;
        setCurrentTaskId(taskId);
        
        // Start polling with proper interval management
        const pollInterval = setInterval(async () => {
          try {
            const statusResponse = await axios.get(
              `${getApiUrl()}/posts/task-status/${taskId}`,
              {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem('token')}`,
                },
              }
            );

            const taskStatus = statusResponse.data.data;
            
            if (taskStatus.done) {
              clearInterval(pollInterval);
              setIsGenerating(false);
              setCurrentTaskId(null);
              
              if (taskStatus.result?.success) {
                addNotification({
                  message: 'Post generated successfully!',
                  type: 'success'
                });
                // Refresh the posts lists
                await fetchPosts();
              } else {
                setGenerationError(taskStatus.result?.error || 'Failed to generate post');
                addNotification({
                  message: taskStatus.result?.error || 'Failed to generate post',
                  type: 'error'
                });
              }
            }
          } catch (error) {
            clearInterval(pollInterval);
            setIsGenerating(false);
            setCurrentTaskId(null);
            addNotification({
              message: 'Error checking task status',
              type: 'error'
            });
          }
        }, 5000); // Poll every 5 seconds

        // Clear polling after 10 minutes maximum
        setTimeout(() => {
          clearInterval(pollInterval);
          if (isGenerating) {
            setIsGenerating(false);
            setCurrentTaskId(null);
            addNotification({
              message: 'Post generation timed out. Please try again.',
              type: 'warning'
            });
          }
        }, 600000);
      }
    } catch (error) {
      setIsGenerating(false);
      addNotification({
        message: error.response?.data?.error || 'Failed to start post generation',
        type: 'error'
      });
    }
  };

  // Check for existing task on component mount
  useEffect(() => {
    const existingTaskId = localStorage.getItem('currentGenerationTaskId');
    if (existingTaskId) {
      setCurrentTaskId(existingTaskId);
      addNotification({
        message: 'Generating blog post... This may take a few minutes.',
        type: 'info'
      });
      pollTaskStatus(existingTaskId);
    }
  }, []);

  // Add this helper function to refresh posts
  const fetchPosts = async () => {
    const token = localStorage.getItem('token');
    try {
      const [recentResponse, upcomingResponse] = await Promise.all([
        axios.get(`${getApiUrl()}/posts/published-posts`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${getApiUrl()}/posts/scheduled-posts`, {
          headers: { Authorization: `Bearer ${token}` },
        })
      ]);
      
      setRecentPosts(recentResponse.data);
      setUpcomingPosts(upcomingResponse.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
    }
  };

  return (
    <Container maxWidth="lg">
      <PageHead 
        title="Content Generation" 
        description="Generate AI-powered blog content with AutoBlogPilot"
      />
      <Box
        sx={{
          background: 'linear-gradient(145deg, #f6f8ff 0%, #f0f4ff 100%)',
          borderRadius: 4,
          p: 4,
          minHeight: '90vh',
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Box mb={4}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography
                variant="h3"
                component="h1"
                gutterBottom
                sx={{
                  fontWeight: 'bold',
                  background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                  backgroundClip: 'text',
                  textFillColor: 'transparent',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Content Generation
              </Typography>
              <Button
                variant="contained"
                onClick={handleCreatePost}
                disabled={isGenerating}
                startIcon={isGenerating ? <CircularProgress size={20} /> : <AutoAwesomeIcon />}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                  boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)',
                  px: 3,
                  py: 1,
                }}
              >
                {isGenerating ? 'Generating...' : 'Generate New Post'}
              </Button>
            </Box>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Configure your content settings and generate new posts
            </Typography>
          </Box>

          {generationError && (
            <Alert
              severity="error"
              sx={{
                mb: 3,
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              }}
            >
              {generationError}
            </Alert>
          )}

          {/* Content Settings Card */}
          <Paper
            elevation={2}
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              background: 'white',
              mb: 4,
            }}
          >
            <Box p={3}>
              <Box display="flex" alignItems="center" mb={2}>
                <TuneIcon sx={{ mr: 2, color: 'primary.main' }} />
                <Typography variant="h5" component="h2" fontWeight="bold">
                  Content Settings
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              
              {/* Frequency Setting */}
              <Box mb={3}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Box display="flex" alignItems="center">
                    <ScheduleIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="subtitle1" fontWeight="medium">
                      Posting Frequency
                    </Typography>
                  </Box>
                  <Tooltip title="Edit frequency">
                    <IconButton
                      onClick={() => handleEditSetting('frequency')}
                      size="small"
                      sx={{ 
                        background: 'rgba(33, 150, 243, 0.1)',
                        '&:hover': { background: 'rgba(33, 150, 243, 0.2)' }
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography variant="body1" color="text.secondary">
                  {settings.frequency || 'Not set'}
                </Typography>
              </Box>

              {/* Topic Setting */}
              <Box mb={3}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Box display="flex" alignItems="center">
                    <TopicIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="subtitle1" fontWeight="medium">
                      Content Topic
                    </Typography>
                  </Box>
                  <Tooltip title="Edit topic">
                    <IconButton
                      onClick={() => handleEditSetting('topic')}
                      size="small"
                      sx={{ 
                        background: 'rgba(33, 150, 243, 0.1)',
                        '&:hover': { background: 'rgba(33, 150, 243, 0.2)' }
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography variant="body1" color="text.secondary">
                  {settings.topic || 'Not set'}
                </Typography>
              </Box>

              {/* Tone Setting */}
              <Box mb={3}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Box display="flex" alignItems="center">
                    <AutoAwesomeIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="subtitle1" fontWeight="medium">
                      Writing Tone
                    </Typography>
                  </Box>
                  <Tooltip title="Edit tone">
                    <IconButton
                      onClick={() => handleEditSetting('tone')}
                      size="small"
                      sx={{ 
                        background: 'rgba(33, 150, 243, 0.1)',
                        '&:hover': { background: 'rgba(33, 150, 243, 0.2)' }
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography variant="body1" color="text.secondary">
                  {settings.tone || 'Not set'}
                </Typography>
              </Box>

              {/* SEO Focus Setting */}
              <Box mb={3}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Box display="flex" alignItems="center">
                    <SearchIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="subtitle1" fontWeight="medium">
                      SEO Focus
                    </Typography>
                  </Box>
                  <Tooltip title="Edit SEO focus">
                    <IconButton
                      onClick={() => handleEditSetting('seoFocus')}
                      size="small"
                      sx={{ 
                        background: 'rgba(33, 150, 243, 0.1)',
                        '&:hover': { background: 'rgba(33, 150, 243, 0.2)' }
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography variant="body1" color="text.secondary">
                  {settings.seoFocus.length > 0 ? settings.seoFocus.join(', ') : 'Not set'}
                </Typography>
              </Box>

              {/* CTA Setting */}
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Box display="flex" alignItems="center">
                    <CallIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="subtitle1" fontWeight="medium">
                      Call to Action
                    </Typography>
                  </Box>
                  <Tooltip title="Edit CTA">
                    <IconButton
                      onClick={() => handleEditSetting('cta')}
                      size="small"
                      sx={{ 
                        background: 'rgba(33, 150, 243, 0.1)',
                        '&:hover': { background: 'rgba(33, 150, 243, 0.2)' }
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    background: 'rgba(0, 0, 0, 0.03)',
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <Button
                    variant="contained"
                    sx={{
                      backgroundColor: settings.ctaButtonColor || '#1976d2',
                      color: settings.ctaTextColor || '#ffffff',
                      '&:hover': {
                        backgroundColor: settings.ctaButtonColor 
                          ? `${settings.ctaButtonColor}dd`
                          : '#1565c0',
                      },
                    }}
                  >
                    {settings.ctaText || 'Click Here'}
                  </Button>
                </Box>
              </Box>
            </Box>
          </Paper>

          {/* Posts Overview */}
          <Grid container spacing={3}>
            {/* Published Posts */}
            <Grid item xs={12} md={6}>
              <Paper 
                elevation={2}
                sx={{ 
                  borderRadius: 3,
                  overflow: 'hidden',
                  background: 'white',
                  height: '100%',
                }}
              >
                <Box p={3}>
                  <Box display="flex" alignItems="center" mb={2}>
                    <Box
                      sx={{
                        background: 'rgba(76, 175, 80, 0.1)',
                        borderRadius: '50%',
                        p: 1,
                        mr: 2,
                        color: 'success.main',
                      }}
                    >
                      <CheckCircleIcon />
                    </Box>
                    <Typography variant="h6" component="h3" fontWeight="medium">
                      Published Posts
                    </Typography>
                  </Box>
                  
                  {recentPosts.length > 0 ? (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.5 }}
                    >
                      {recentPosts.map((post, index) => (
                        <motion.div
                          key={post.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.1 }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              p: 2,
                              mb: 1,
                              borderRadius: 2,
                              background: 'rgba(76, 175, 80, 0.05)',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                transform: 'translateX(8px)',
                                background: 'rgba(76, 175, 80, 0.1)',
                              },
                            }}
                          >
                            <Box>
                              <Typography variant="body1" fontWeight="medium" color="text.primary" gutterBottom>
                                {post.title}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Published {new Date(post.published_date).toLocaleDateString()} at {new Date(post.published_date).toLocaleTimeString()}
                              </Typography>
                            </Box>
                            <IconButton 
                              component="a"
                              href={`${blogUrl ? `https://${blogUrl.replace(/^https?:\/\//, '')}` : ''}/${post.url_slug}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ 
                                color: 'success.main',
                                '&:hover': { 
                                  color: 'success.dark',
                                  background: 'rgba(76, 175, 80, 0.1)'
                                }
                              }}
                            >
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </motion.div>
                      ))}
                    </motion.div>
                  ) : (
                    <Box 
                      sx={{ 
                        textAlign: 'center',
                        py: 4,
                        background: 'rgba(0, 0, 0, 0.02)',
                        borderRadius: 2
                      }}
                    >
                      <Typography color="text.secondary">No published posts yet</Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Grid>

            {/* Scheduled Posts */}
            <Grid item xs={12} md={6}>
              <Paper 
                elevation={2}
                sx={{ 
                  borderRadius: 3,
                  overflow: 'hidden',
                  background: 'white',
                  height: '100%',
                }}
              >
                <Box p={3}>
                  <Box display="flex" alignItems="center" mb={2}>
                    <Box
                      sx={{
                        background: 'rgba(33, 150, 243, 0.1)',
                        borderRadius: '50%',
                        p: 1,
                        mr: 2,
                        color: 'primary.main',
                      }}
                    >
                      <ScheduleIcon />
                    </Box>
                    <Typography variant="h6" component="h3" fontWeight="medium">
                      Scheduled Posts
                    </Typography>
                  </Box>

                  {upcomingPosts.length > 0 ? (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.5 }}
                    >
                      {upcomingPosts.map((post, index) => (
                        <motion.div
                          key={post.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.1 }}
                        >
                          <Box
                            sx={{
                              p: 2,
                              mb: 1,
                              borderRadius: 2,
                              background: 'rgba(33, 150, 243, 0.05)',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                transform: 'translateX(8px)',
                                background: 'rgba(33, 150, 243, 0.1)',
                              },
                            }}
                          >
                            <Typography variant="body1" fontWeight="medium" color="text.primary" gutterBottom>
                              {post.title}
                            </Typography>
                            <Box display="flex" alignItems="center" gap={1}>
                              <ScheduleIcon fontSize="small" sx={{ color: 'primary.main' }} />
                              <Typography variant="body2" color="text.secondary">
                                Scheduled for {new Date(post.scheduled_date).toLocaleDateString()} at {new Date(post.scheduled_date).toLocaleTimeString()}
                              </Typography>
                            </Box>
                          </Box>
                        </motion.div>
                      ))}
                    </motion.div>
                  ) : (
                    <Box 
                      sx={{ 
                        textAlign: 'center',
                        py: 4,
                        background: 'rgba(0, 0, 0, 0.02)',
                        borderRadius: 2
                      }}
                    >
                      <Typography color="text.secondary">No scheduled posts</Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </motion.div>
      </Box>

      {/* Settings Dialog */}
      <Dialog
        open={isEditing}
        onClose={() => setIsEditing(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h5" component="h2" fontWeight="bold">
            Edit {editField?.charAt(0).toUpperCase() + editField?.slice(1)}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {editField === 'frequency' && (
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedFrequencyDays?.['Daily'] || false}
                    onChange={(e) => {
                      const newDays = { ...selectedFrequencyDays };
                      newDays['Daily'] = e.target.checked;
                      if (e.target.checked) {
                        Object.keys(newDays).forEach((day) => {
                          newDays[day] = true;
                        });
                      }
                      setSelectedFrequencyDays(newDays);
                    }}
                  />
                }
                label="Daily"
              />
              {!selectedFrequencyDays?.['Daily'] &&
                Object.keys(selectedFrequencyDays || {})
                  .filter((day) => day !== 'Daily')
                  .map((day) => (
                    <FormControlLabel
                      key={day}
                      control={
                        <Checkbox
                          checked={selectedFrequencyDays?.[day] || false}
                          onChange={(e) => {
                            setSelectedFrequencyDays({
                              ...selectedFrequencyDays,
                              [day]: e.target.checked,
                            });
                          }}
                        />
                      }
                      label={day}
                    />
                  ))}
            </FormGroup>
          )}
          {editField === 'seoFocus' && (
            <FormGroup>
              {seoFocusOptions.map((option) => (
                <FormControlLabel
                  key={option}
                  control={
                    <Checkbox
                      checked={selectedSeoFocusOptions?.[option] || false}
                      onChange={(e) => {
                        setSelectedSeoFocusOptions({
                          ...selectedSeoFocusOptions,
                          [option]: e.target.checked,
                        });
                      }}
                    />
                  }
                  label={option}
                />
              ))}
            </FormGroup>
          )}
          {editField === 'cta' && (
            <Box>
              <TextField
                fullWidth
                label="CTA Text"
                value={editValue.ctaText}
                onChange={(e) =>
                  setEditValue({ ...editValue, ctaText: e.target.value })
                }
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Button Color"
                type="color"
                value={editValue.ctaButtonColor}
                onChange={(e) =>
                  setEditValue({ ...editValue, ctaButtonColor: e.target.value })
                }
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Text Color"
                type="color"
                value={editValue.ctaTextColor}
                onChange={(e) =>
                  setEditValue({ ...editValue, ctaTextColor: e.target.value })
                }
              />
            </Box>
          )}
          {(editField === 'tone' || editField === 'topic') && (
            <TextField
              fullWidth
              label={editField.charAt(0).toUpperCase() + editField.slice(1)}
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setIsEditing(false)}
            sx={{
              color: 'text.secondary',
              '&:hover': { background: 'rgba(0,0,0,0.05)' },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveSetting}
            variant="contained"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
              boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)',
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default ContentGeneration;
